import { useCallback, useEffect, useRef, useState } from 'react';

// SC-072 review round 2 (Wren, CRITICAL/HIGH): the cursor-stack pagination
// mechanics are pulled out of ProductList.tsx into this hook so the
// error/reset/race-guard logic is unit-testable in isolation from the DOM.
// See ProductList.tsx for the concrete `fetchPage` wiring against
// productsApi.getAll.

export type CursorFetchResult<T> =
  | { kind: 'success'; data: T[]; nextCursor: string | null }
  // A cursor was sent and the server rejected it (expired/tampered/
  // query-mismatched, HTTP 400). The caller supplying `fetchPage` is
  // responsible for only ever returning this when a cursor was actually
  // in play: a cursorless request that 400s for another reason must be
  // classified `error`, never `cursorRejected` (fix 2 below).
  | { kind: 'cursorRejected' }
  | { kind: 'error'; message: string };

export type FetchPage<T> = (cursor: string | undefined) => Promise<CursorFetchResult<T>>;

export interface UseCursorPaginationResult<T> {
  items: T[];
  loading: boolean;
  error: string | null;
  pageIndex: number;
  canGoNext: boolean;
  canGoPrev: boolean;
  goNext: () => Promise<void>;
  goPrev: () => Promise<void>;
  retry: () => Promise<void>;
  refresh: () => Promise<void>;
}

interface Attempt {
  cursor: string | undefined;
  targetPageIndex: number;
  historyForCommit: Array<string | undefined>;
  // SC-078: was this attempt the resetKey-triggered fetch (a new query's
  // fresh first page), as opposed to a same-query Next/Prev/retry? A
  // failed reset attempt has no valid "last-good page" to fall back to -
  // the last-good page belongs to the OLD query.
  isReset: boolean;
  // SC-077 (Wren LOW): was this attempt itself the cursorless auto-retry
  // fired from the `cursorRejected` branch below? Retrying an attempt
  // that already used up its one auto-retry must never be allowed to
  // recurse again, even if `fetchPage` misbehaves and returns
  // `cursorRejected` a second time for a cursorless request.
  wasCursorRejectedRetry: boolean;
}

const FIRST_PAGE_ATTEMPT: Attempt = {
  cursor: undefined,
  targetPageIndex: 0,
  historyForCommit: [undefined],
  isReset: true,
  wasCursorRejectedRetry: false,
};

/**
 * Cursor-based (forward-only) pagination state machine.
 *
 * - `resetKey` changing (e.g. a search term) starts a fresh, cursorless
 *   walk at page 1: a `cursor` from one query must never be replayed
 *   against a different one.
 * - Page state (`pageIndex` / the cursor stack / `items`) only advances on
 *   a *successful* fetch (fix 4): a failed Next/Prev/retry leaves the
 *   currently-displayed page, and the cursor it took to reach it,
 *   untouched - the header and the grid can never disagree about which
 *   page is showing.
 * - A monotonic request sequence number discards a response that is no
 *   longer the latest in-flight request (fix 3): a slow page-2 fetch that
 *   resolves after a reset (the user changed the search term while it was
 *   in flight) can never overwrite the newer result.
 * - `cursorRejected` triggers exactly one retry at a cursorless first
 *   page. That retry is structurally incapable of itself being classified
 *   `cursorRejected` (no cursor is in play), so this can never loop even
 *   if page 1 also fails (fix 2) - it surfaces as a plain `error` instead.
 *   A manual Retry of that already-retried attempt is structurally barred
 *   from re-enabling the auto-retry (SC-077): even a misbehaving
 *   `fetchPage` that returns `cursorRejected` again can't recurse further.
 * - A RESET fetch (resetKey changed: a new search term) failing has no
 *   valid last-good page to preserve (SC-078): the previously-displayed
 *   items belong to the OLD query, so a failed reset clears them rather
 *   than leaving stale old-query items on screen under the new query's
 *   caption. A same-query Next/Prev/retry failure still preserves the
 *   last-good page exactly as before.
 * - `refresh()` re-fetches the CURRENT page/query (e.g. after a delete)
 *   and is always dispatched with `isReset: false` (SC-078 review, Wren
 *   HIGH), never by replaying whatever `isReset` the last attempt
 *   happened to carry. `retry()` replays the last ATTEMPT verbatim
 *   (including its `isReset`), which is correct for "try that same
 *   failed fetch again" but wrong for "reload the list after an
 *   unrelated action succeeded": on page 1, the last attempt is the
 *   mount/search-reset load (`isReset: true`), so calling `retry()` to
 *   refresh after a delete would misclassify a transient refresh
 *   failure as a reset failure and wipe a still-valid product list.
 *   `refresh()` exists so a caller reloading the current page can never
 *   make that mistake: a failed refresh behaves like a same-query
 *   failure (last-good page preserved, non-destructive inline banner).
 */
export function useCursorPagination<T>(fetchPage: FetchPage<T>, resetKey: unknown): UseCursorPaginationResult<T> {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [cursorHistory, setCursorHistory] = useState<Array<string | undefined>>([undefined]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  // Read via a ref so `fetchAndCommit` can stay referentially stable
  // (empty dep array) without going stale: the reset effect below only
  // needs to fire on `resetKey`, not on every re-creation of `fetchPage`.
  const fetchPageRef = useRef(fetchPage);
  useEffect(() => {
    fetchPageRef.current = fetchPage;
  }, [fetchPage]);

  const requestSeqRef = useRef(0);
  const lastAttemptRef = useRef<Attempt>(FIRST_PAGE_ATTEMPT);

  const fetchAndCommit = useCallback(
    async (
      cursor: string | undefined,
      targetPageIndex: number,
      historyForCommit: Array<string | undefined>,
      allowCursorRejectedRetry: boolean,
      isReset: boolean
    ): Promise<void> => {
      lastAttemptRef.current = {
        cursor,
        targetPageIndex,
        historyForCommit,
        isReset,
        wasCursorRejectedRetry: !allowCursorRejectedRetry,
      };

      const seq = ++requestSeqRef.current;
      setLoading(true);
      setError(null);

      let result: CursorFetchResult<T>;
      try {
        result = await fetchPageRef.current(cursor);
      } catch (thrown) {
        // `fetchPage` is expected to resolve to a discriminated result, not
        // throw - but a network-level throw must still surface as a
        // handled error state, not an unhandled rejection that leaves
        // `loading` stuck true forever.
        console.error('useCursorPagination: fetchPage threw', thrown);
        result = {
          kind: 'error',
          message: thrown instanceof Error ? thrown.message : 'Failed to load products.',
        };
      }

      // A newer request (Next/Prev/reset/retry) has since started: this
      // response is stale and must never commit over it.
      if (seq !== requestSeqRef.current) {
        return;
      }

      // Do not touch pageIndex/cursorHistory/items on anything but success
      // (fix 4): the last successful page stays the source of truth on
      // screen while an error is surfaced distinctly from an empty catalog.
      switch (result.kind) {
        case 'success': {
          setItems(result.data);
          setNextCursor(result.nextCursor);
          setCursorHistory(historyForCommit);
          setPageIndex(targetPageIndex);
          setLoading(false);
          return;
        }
        case 'cursorRejected': {
          if (allowCursorRejectedRetry) {
            await fetchAndCommit(undefined, 0, [undefined], false, isReset);
            return;
          }
          // Defensive only: the `fetchPage` contract (fix 2) guarantees a
          // cursorless retry can never itself be `cursorRejected`, so this
          // branch should be unreachable in practice. Surface it as an
          // error rather than silently looping if it ever is reached.
          if (isReset) {
            setItems([]);
            setNextCursor(null);
            setCursorHistory([undefined]);
            setPageIndex(0);
          }
          setError('Unable to load products: invalid pagination state.');
          setLoading(false);
          return;
        }
        case 'error': {
          // SC-078: a failed RESET fetch (new query, e.g. a changed search
          // term) has no valid last-good page to preserve - the items
          // still in state belong to the OLD query. Clear them so the UI
          // can't render old-query products under the new query's page
          // caption. A same-query Next/Prev/retry failure is unaffected:
          // it still preserves the last-good page (fix 4), matched below
          // by leaving items/pageIndex/cursorHistory untouched.
          if (isReset) {
            setItems([]);
            setNextCursor(null);
            setCursorHistory([undefined]);
            setPageIndex(0);
          }
          setError(result.message);
          setLoading(false);
          return;
        }
        default: {
          // Exhaustiveness guard (js-ts.md rule 8): a future 4th variant of
          // CursorFetchResult fails to compile here instead of silently
          // falling through to a generic message.
          const _exhaustive: never = result;
          throw new Error(`useCursorPagination: unhandled CursorFetchResult: ${JSON.stringify(_exhaustive)}`);
        }
      }
    },
    []
  );

  useEffect(() => {
    void fetchAndCommit(undefined, 0, [undefined], true, true);
    // `fetchAndCommit` has a stable identity (empty deps above), so this
    // effect only re-runs when `resetKey` itself changes.
  }, [resetKey, fetchAndCommit]);

  const canGoNext = nextCursor !== null;
  const canGoPrev = pageIndex > 0;

  const goNext = useCallback(async () => {
    if (nextCursor === null) return;
    const targetPageIndex = pageIndex + 1;
    const historyForCommit =
      cursorHistory[targetPageIndex] === nextCursor
        ? cursorHistory
        : [...cursorHistory.slice(0, targetPageIndex), nextCursor];
    await fetchAndCommit(nextCursor, targetPageIndex, historyForCommit, true, false);
  }, [nextCursor, pageIndex, cursorHistory, fetchAndCommit]);

  const goPrev = useCallback(async () => {
    if (pageIndex === 0) return;
    const targetPageIndex = pageIndex - 1;
    await fetchAndCommit(cursorHistory[targetPageIndex], targetPageIndex, cursorHistory, true, false);
  }, [pageIndex, cursorHistory, fetchAndCommit]);

  // Re-fetches the CURRENT page/query - e.g. to reload the list after an
  // unrelated action (a delete) succeeded. Always dispatched with
  // `isReset: false` (SC-078 review, Wren HIGH): unlike `retry()`, this
  // never inherits whatever `isReset` the last ATTEMPT happened to carry,
  // so a refresh can never be misclassified as a failed search-reset and
  // wipe a still-valid list. A failed refresh behaves like a same-query
  // failure: the last-good page and its items stay on screen behind a
  // non-destructive inline banner.
  const refresh = useCallback(async () => {
    await fetchAndCommit(cursorHistory[pageIndex], pageIndex, cursorHistory, true, false);
  }, [pageIndex, cursorHistory, fetchAndCommit]);

  // Replays whichever fetch was last ATTEMPTED (not just last committed):
  // after a failed Next, Retry retries the Next, not a no-op reload of the
  // page already on screen; after a failed reset (e.g. a search term that
  // 400s on a fresh, cursorless first page), Retry replays that same
  // cursorless request bound to the new query, not the stale pre-reset
  // page/cursor.
  //
  // SC-077 (Wren LOW): the auto-retry-on-cursorRejected guarantee is
  // enforced here structurally, not just by the `fetchPage` contract - a
  // manual Retry of an attempt that was ITSELF the cursorless auto-retry
  // is dispatched with `allowCursorRejectedRetry: false`, so it can never
  // re-enable another level of recursion even if `fetchPage` returns
  // `cursorRejected` again for a cursorless request.
  const retry = useCallback(async () => {
    const { cursor, targetPageIndex, historyForCommit, isReset, wasCursorRejectedRetry } = lastAttemptRef.current;
    await fetchAndCommit(cursor, targetPageIndex, historyForCommit, !wasCursorRejectedRetry, isReset);
  }, [fetchAndCommit]);

  return { items, loading, error, pageIndex, canGoNext, canGoPrev, goNext, goPrev, retry, refresh };
}
